import { IconButton, InputAdornment } from '@mui/material';
import React, { useCallback } from 'react';
import './ChatMessageInputBox.scss';
import SendIcon from '@mui/icons-material/Send';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import AttachmentIcon from '@mui/icons-material/Attachment';
import Chip from '@mui/material/Chip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { toBase64 } from '@utils/file-helper';
import { useAPI } from '@components/APIProvider';
import { randomString } from '@utils/random';
import CircularProgress from '@mui/material/CircularProgress';
import ImageIcon from '@mui/icons-material/Image';
import AudioFileIcon from '@mui/icons-material/AudioFile';

export const ChatMessageInputBox = ({
  onSendMessage,
  webChatConfiguration,
}) => {
  const api = useAPI();
  const [message, setMessage] = React.useState('');
  const [attachments, setAttachments] = React.useState([]);

  const handleSendMessage = useCallback(() => {
    const uploadedAttachments = attachments.filter((attachment) => {
      return !attachment.isUploading;
    });
    const remainingAttachments = attachments.filter((attachment) => {
      return attachment.isUploading;
    });
    let hasAnythingToSend =
      Boolean(message.trim()) || uploadedAttachments.length > 0;

    console.log('size', message.trim().length, uploadedAttachments.length);

    if (onSendMessage && hasAnythingToSend) {
      // eslint-disable-next-line array-callback-return
      const contentBlocks = uploadedAttachments.map((attachment) => {
        if (attachment.image_id) {
          return {
            type: 'image',
            image_id: attachment.image_id,
          };
        } else if (attachment.file_id) {
          return {
            type: 'file',
            file_id: attachment.file_id,
          };
        }
      });

      onSendMessage(message, contentBlocks);
      setMessage('');
      setAttachments(remainingAttachments);
    }
  }, [message, attachments, onSendMessage]);

  const handleInputChange = useCallback((event) => {
    setMessage(event.target.value);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter') {
        // Check if ctrl or shift is being held, and if so, allow the enter key to go through normally
        if (event.ctrlKey || event.shiftKey) {
          // Allow the default behavior of adding a new line
          return;
        } else {
          event.preventDefault();
          handleSendMessage();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [message, onSendMessage, setMessage, handleSendMessage]
  );

  const handleSendClick = useCallback(() => {
    handleSendMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, onSendMessage, handleSendMessage]);

  const handleImageUpload = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (file) {
        const attachment = {
          attachmentId: randomString(16),
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isUploading: true,
        };

        setAttachments((prevAttachments) => [...prevAttachments, attachment]);

        try {
          const base64Image = await toBase64(file);
          const base64String = base64Image.split(',')[1];
          const payload = {
            file: base64String,
          };
          const response = await api.uploadImage(payload);
          if (response && response._id) {
            attachment.isUploading = false;
            attachment.image_id = response._id;
            setAttachments((prevAttachments) =>
              prevAttachments.map((prevAttachment) => {
                if (prevAttachment.attachmentId === attachment.attachmentId) {
                  return {
                    ...prevAttachment,
                    ...attachment,
                  };
                }
                return prevAttachment;
              })
            );
          }
        } catch (error) {
        } finally {
          e.target.value = null;
        }
      }
    },
    [api]
  );

  const handleFileUpload = useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (file) {
        const attachment = {
          attachmentId: randomString(16),
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          isUploading: true,
        };

        setAttachments((prevAttachments) => [...prevAttachments, attachment]);

        try {
          const response = await api.uploadFile(file);
          if (response && response._id) {
            attachment.isUploading = false;
            attachment.file_id = response._id;
            setAttachments((prevAttachments) =>
              prevAttachments.map((prevAttachment) => {
                if (prevAttachment.attachmentId === attachment.attachmentId) {
                  return {
                    ...prevAttachment,
                    ...attachment,
                  };
                }
                return prevAttachment;
              })
            );
          }
        } catch (error) {
        } finally {
          e.target.value = null;
        }
      }
    },
    [api]
  );

  const handleAttachClicked = useCallback(() => {
    // Show the user a file dialog to select a file.
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    // Only allow images, audio, and documents.
    const mimeTypes = [
      'image/*',
      'audio/*',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ];
    fileInput.accept = mimeTypes.join(',');

    fileInput.onchange = (event) => {
      if (event.target.files.length > 0) {
        const file = event.target.files[0];
        // Check if the file is too large
        if (file.size > 50 * 1024 * 1024) {
          // Show an error message to the user
          alert('File size exceeds 50MB');
          return;
        }

        if (file.type.startsWith('image/')) {
          handleImageUpload(event);
        } else if (file.type.startsWith('audio/')) {
          // Handle audio file upload
        } else {
          // Handle document upload.
          handleFileUpload(event);
        }
      }
    };
    fileInput.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteAttachment = useCallback((attachment) => {
    setAttachments((prevAttachments) =>
      prevAttachments.filter((a) => a.name !== attachment.name)
    );
  }, []);

  return (
    <FormControl
      variant="outlined"
      className={'prospera-chat-message-input-box'}
      fullWidth
      sx={{ width: '100%' }}
    >
      <OutlinedInput
        id="message-input"
        type={'text'}
        multiline={true}
        autosize={'true'}
        value={message}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={'Type your message here...'}
        sx={{
          ...(webChatConfiguration?.input_box_background_color
            ? {
                backgroundColor:
                  webChatConfiguration.input_box_background_color,
              }
            : {}),
          ...(webChatConfiguration?.input_box_text_color
            ? { color: webChatConfiguration.input_box_text_color }
            : {}),
          ...(webChatConfiguration?.input_box_border_radius != null
            ? {
                borderRadius: `${webChatConfiguration.input_box_border_radius}px`,
              }
            : {}),
          // Target the actual textarea/input elements inside MUI OutlinedInput
          // so the custom text color overrides MUI's theme color.
          '& textarea, & input': {
            ...(webChatConfiguration?.input_box_text_color
              ? {
                  color: `${webChatConfiguration.input_box_text_color} !important`,
                  WebkitTextFillColor: `${webChatConfiguration.input_box_text_color} !important`,
                }
              : {}),
          },
          '& fieldset': {
            ...(webChatConfiguration?.input_box_border_color
              ? {
                  borderColor: `${webChatConfiguration.input_box_border_color} !important`,
                }
              : {}),
            // Hotfix 7: Restore full 4-sided border when custom background is set.
            // SCSS defaults remove left/right/bottom borders for the light-theme flush look,
            // but on dark/custom backgrounds this appears broken. Only activates when
            // input_box_background_color is configured (non-null) — retrocompatible.
            ...(webChatConfiguration?.input_box_background_color
              ? {
                  borderWidth: '1px !important',
                  ...(webChatConfiguration?.input_box_border_radius != null
                    ? {
                        borderRadius: `${webChatConfiguration.input_box_border_radius}px !important`,
                      }
                    : {}),
                }
              : {}),
          },
          '& input::placeholder, & textarea::placeholder': {
            ...(webChatConfiguration?.input_box_placeholder_color
              ? {
                  color: `${webChatConfiguration.input_box_placeholder_color} !important`,
                  opacity: '1 !important',
                  WebkitTextFillColor: `${webChatConfiguration.input_box_placeholder_color} !important`,
                }
              : {}),
          },
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label={'Attach File'}
              onClick={handleAttachClicked}
              color={message ? 'info' : 'default'}
              sx={{
                ...(webChatConfiguration?.input_box_icon_color
                  ? {
                      color: `${webChatConfiguration.input_box_icon_color} !important`,
                    }
                  : {}),
              }}
            >
              <AttachmentIcon />
            </IconButton>
            <IconButton
              aria-label={'Send Message'}
              onClick={handleSendClick}
              color={message ? 'info' : 'default'}
              edge="end"
              sx={{
                ...(webChatConfiguration?.input_box_icon_color
                  ? {
                      color: `${webChatConfiguration.input_box_icon_color} !important`,
                    }
                  : {}),
              }}
            >
              <SendIcon />
            </IconButton>
          </InputAdornment>
        }
      />
      {attachments.length > 0 ? (
        <div className={'prospera-chat-attachments-area'}>
          {attachments.map((attachment, attachmentIndex) => {
            return (
              <Chip
                key={attachmentIndex}
                icon={
                  attachment.isUploading ? (
                    <CircularProgress size={18} />
                  ) : (
                    getIconForFile(attachment)
                  )
                }
                label={attachment.name}
                onDelete={handleDeleteAttachment.bind(null, attachment)}
              />
            );
          })}
        </div>
      ) : null}
    </FormControl>
  );
};

function getIconForFile(file) {
  if (file.type.startsWith('image/')) {
    return <ImageIcon />;
  } else if (file.type.startsWith('audio/')) {
    return <AudioFileIcon />;
  } else {
    return <UploadFileIcon />;
  }
}
